import { FC } from "react";
import { Entity } from "excalibur";
import { Card, CardId } from "../types";
import Hand from "./Hand";
import type { HandCombiningInfo } from "../deck";
import { TargetableComponent } from "../ecs/target";
import { BaseFlow } from "../engine/baseFlow";
import { isMobile } from "../utils/mobile";

export type HandDisplayProps = {
  hand: Card[];
  drawPileSize: number;
  maxHandSize: number;
  canDraw: boolean;
  onDrawCard: () => void;
  swapCards: (a: CardId, b: CardId) => void;
  moveCard?: (cardId: CardId, newIndex: number) => void;
  onUndo?: () => void;
  canUndo?: boolean;
  undoDescription?: string;
  combiningInfo?: HandCombiningInfo;
  onShopClick?: () => void;
  hoverTarget?: Entity<TargetableComponent>;
};

const HandDisplay: FC<HandDisplayProps> = ({
  hand,
  drawPileSize,
  maxHandSize,
  canDraw,
  onDrawCard,
  swapCards,
  moveCard,
  onUndo,
  canUndo = true,
  undoDescription,
  combiningInfo,
  onShopClick,
  hoverTarget,
}) => {
  // Check if there's an active hover preview (when hoverTarget exists)
  const hasActiveMobilePreview = isMobile() && !!hoverTarget;

  return (
    <div>
      <div className="flex flex-wrap gap-2 sm:gap-4 items-center mb-3 text-sm sm:text-base">
        <div>Deck: {drawPileSize}</div>
        <button
          className={`px-2 py-1 sm:px-3 rounded text-xs sm:text-sm ${
            canDraw
              ? "bg-blue-500 hover:bg-blue-600 text-white"
              : "bg-gray-400 text-gray-600 cursor-not-allowed"
          }`}
          onClick={onDrawCard}
          disabled={!canDraw}
          title={!canDraw ? "Hand is full or no cards in deck" : "Draw a card"}
        >
          Draw Card
        </button>
        <div>
          Hand: {hand.length}/{maxHandSize}
        </div>

        {onShopClick && (
          <button
            className="px-2 py-1 sm:px-3 rounded text-xs sm:text-sm bg-lime-800 hover:bg-lime-900 text-white"
            onClick={onShopClick}
            title="Open shop to buy card packs"
          >
            Shop
          </button>
        )}
        {onUndo && (
          <button
            className={`px-2 py-1 sm:px-3 rounded text-xs sm:text-sm ${
              hasActiveMobilePreview
                ? "bg-red-800 hover:bg-red-900 text-white"
                : canUndo
                  ? "bg-yellow-800 hover:bg-yellow-900 text-white"
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
            }`}
            onClick={
              hasActiveMobilePreview
                ? () => BaseFlow.CancelCurrentFlow()
                : onUndo
            }
            disabled={hasActiveMobilePreview ? false : !canUndo}
            title={
              hasActiveMobilePreview
                ? "Cancel placement"
                : undoDescription
                  ? `Undo: ${undoDescription}`
                  : "Undo last action"
            }
          >
            {hasActiveMobilePreview ? "Cancel" : "Undo"}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        {hand.length > 0 ? (
          <Hand
            cards={hand}
            swapCards={swapCards}
            moveCard={moveCard}
            combiningInfo={combiningInfo}
            hoverTarget={hoverTarget}
          />
        ) : (
          <div className="text-gray-400 text-xs sm:text-sm">
            No cards in hand
          </div>
        )}
      </div>
    </div>
  );
};

export default HandDisplay;
